import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import dayjs from 'dayjs';
import useErrorMessage from 'hooks/useErrorMessage';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useDeleteGeoRiskMutation, useGetGeoRiskGroupsQuery, useGetGeoRiskListQuery,
  useGetSingleMAInitiateQuery, useGetStateAIGeoRiskQuery,
  useRefreshAIGeoRiskMutation, useUpdateAIGeoRiskMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityConfigGeographicView from './MaterialityConfigGeographicView';

import type IGeoRiskItem from 'store/slices/materialAssessment/interfaces/IGeoRiskItem';

function MaterialityConfigGeographic() {
  const { recordId = '', vcmId = 0 } = useParams();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [valueToggle, setValueToggle] = useState(false);
  const [geographicItems, setGeographicItems] = useState<IGeoRiskItem[]>([]);
  const [observeStatus, setObserveStatus] = useState(false);
  const [orderBy, setOrderBy] = useState<string[]>([]);
  const [refreshAffectedToken, setRefreshAffectedToken] = useState(0);
  const [isWaitingForRefreshState, setIsWaitingForRefreshState] = useState(false);

  const [geographicDefaultItems, setGeographicDefaultItems] = useState<IGeoRiskItem[]>([]);

  const [deleteGeoRisk] = useDeleteGeoRiskMutation();
  const [updateAIGeoRisk, { isLoading: isLoadingUpdate }] = useUpdateAIGeoRiskMutation();
  const [refreshAIGeoRisk, { isLoading: isLoadingRefresh }] = useRefreshAIGeoRiskMutation();
  const [updateSection] = useUpdateSectionMAMutation();

  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();
  const { data: userData } = useUserMeQuery();
  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId });

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    data: stateAIData,
    isFetching: isStateAIFetching,
    refetch: refetchStateAI,
    error: fetchError,
  } = useGetStateAIGeoRiskQuery({ maId: recordId, vcmId: Number(vcmId) }, {
    skip: vcmId === 0,
    refetchOnMountOrArgChange: true,
  });
  const isInternalProcessing = stateAIData?.data?.status === STATUS_SECTIONS.INTERNAL_PROCESSING;
  const isAffectedReady = stateAIData?.data?.affectedUpToDate === true;
  const isAffectedDataOutdated = stateAIData?.data?.affectedUpToDate === false;
  const isRefreshProcessing = observeStatus || isInternalProcessing || isLoadingRefresh || isLoadingUpdate;
  const disableNext = isRefreshProcessing;
  const disableSave = isRefreshProcessing || !isAffectedReady;

  const isLastSearchOutdated = useMemo(() => {
    const lastSearch = stateAIData?.data?.lastSearch;
    if (!lastSearch) {
      return false;
    }

    return dayjs().startOf('day').diff(dayjs(lastSearch).startOf('day'), 'day') >= 1;
  }, [stateAIData?.data?.lastSearch]);

  const {
    data: geoDataItems,
    isFetching: isGeoLoading,
    error: geoError,
    refetch: refetchGeoData,
  } = useGetGeoRiskListQuery({
    offset,
    maId: recordId,
    orderBy,
  });

  const { data: geoDataGroupItems, refetch: refetchGeoRiskGroups } = useGetGeoRiskGroupsQuery({
    maId: recordId,
    vcmId: Number(vcmId),
  }, {
    skip: Number(vcmId) === 0 || Number(vcmId) === -1,
    refetchOnMountOrArgChange: true,
  });

  const errorMessage = useErrorMessage(fetchError || geoError);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  const hasMore = geoDataItems?.data.meta?.hasNext || false;

  const isInitialLoading = isGeoLoading && offset === 0 && geographicItems.length === 0;
  const isLoadingMore = isGeoLoading && offset > 0;

  const onLoadMore = () => {
    setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
  };

  const updateItems = useCallback((prev: IGeoRiskItem[], newDataItems: IGeoRiskItem[]) => {
    if (offset === 0) {
      return newDataItems.map((item) => ({ ...item, custom: true }));
    }
    const existingIds = new Set(prev.map((item) => item.id));
    const newItems = newDataItems
      .filter((item) => !existingIds.has(item.id))
      .map((item) => ({ ...item, custom: true }));
    if (newItems.length === 0) return prev;
    return [...prev, ...newItems];
  }, [offset]);

  useEffect(() => {
    if (geoDataItems && geoDataItems?.data?.items.length > 0) {
      const newDataItems = geoDataItems.data.items;

      setGeographicItems((prev) => updateItems(prev, newDataItems));
      setGeographicDefaultItems((prev) => updateItems(prev, newDataItems));
    }
  }, [geoDataItems, offset, updateItems]);

  const onCancel = () => {
    setOpenModal(false);
    setSelectedItem(-1);
    setIsDeleteModalOpen(false);
  };

  const onOpenModal = () => {
    setOpenModal(!openModal);
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const saveCompetitor = () => {
    if (disableSave) {
      toast.info(i18n.t('materialityAssessment.geographicRefreshSaveBlocked'));
      return;
    }

    if (!isViewOnly) {
      updateSection({
        maId: recordId,
        type: SECTION_TYPE.TOPIC_1_GEOGRAPHIC_RISKS,
        valueChainMapId: Number(vcmId),
        data: {
          isActive: true,
          status: STATUS_SECTIONS.COMPLETED,
        },
      });
    }

    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onChangeToggle = (value: boolean) => {
    if (value && disableNext) {
      return;
    }

    setValueToggle(value);
  };

  const onDeleteItem = (id: number) => {
    setSelectedItem(id);
    setIsDeleteModalOpen(true);
  };

  const onDeleteConfirm = () => {
    deleteGeoRisk({ id: selectedItem, maId: recordId, vcmId: Number(vcmId) }).then(() => {
      toast.success(i18n.t('notifications.deletedGeoRisk'));
      setGeographicItems((prev) => prev.filter((item) => item.id !== selectedItem));
      setGeographicDefaultItems((prev) => prev.filter((item) => item.id !== selectedItem));
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeleteGeoRisk'));
    });
    onCancel();
  };

  const onUpdate = () => {
    setOffset(0);
    setObserveStatus(true);
    setIsWaitingForRefreshState(true);
    if (!valueToggle) {
      if (!isAffectedDataOutdated) {
        setObserveStatus(false);
        setIsWaitingForRefreshState(false);
        return;
      }

      updateAIGeoRisk({ maId: recordId, vcmId: Number(vcmId) }).then(() => {
        toast.success(i18n.t('notifications.updatedGeoRisk'));
        refetchStateAI();
      }).catch(() => {
        toast.error(i18n.t('notifications.errorUpdateGeoRisk'));
        setObserveStatus(false);
        setIsWaitingForRefreshState(false);
      });
      return;
    }

    if (!isLastSearchOutdated) {
      setObserveStatus(false);
      setIsWaitingForRefreshState(false);
      return;
    }

    refreshAIGeoRisk({ maId: recordId, vcmId: Number(vcmId) }).then(() => {
      toast.success(i18n.t('notifications.refreshGeoRisk'));
      refetchStateAI();
    }).catch(() => {
      toast.error(i18n.t('notifications.errorRefreshGeoRisk'));
      setObserveStatus(false);
      setIsWaitingForRefreshState(false);
    });
  };

  useEffect(() => {
    if (!isInternalProcessing) {
      return;
    }

    setValueToggle(false);
    setObserveStatus(true);
    setIsWaitingForRefreshState(false);
  }, [isInternalProcessing]);

  useEffect(() => {
    if (!isWaitingForRefreshState) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      refetchStateAI();
      setIsWaitingForRefreshState(false);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isWaitingForRefreshState, refetchStateAI]);

  useEffect(() => {
    if (!observeStatus || isWaitingForRefreshState || isLoadingUpdate || isLoadingRefresh || isStateAIFetching) {
      return undefined;
    }

    if (isInternalProcessing || !isAffectedReady) {
      const timeoutId = window.setTimeout(() => {
        refetchStateAI();
      }, 15000);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    const timeoutId = window.setTimeout(() => {
      setOffset(0);
      refetchGeoData();
      if (Number(vcmId) !== 0 && Number(vcmId) !== -1) {
        refetchGeoRiskGroups();
      }
      setRefreshAffectedToken((prev) => prev + 1);
      setObserveStatus(false);
    }, 1500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    observeStatus,
    isWaitingForRefreshState,
    isLoadingUpdate,
    isLoadingRefresh,
    isStateAIFetching,
    isInternalProcessing,
    isAffectedReady,
    refetchStateAI,
    refetchGeoData,
    refetchGeoRiskGroups,
    vcmId,
  ]);

  const loading = isInitialLoading || isLoadingUpdate || isStateAIFetching || isLoadingRefresh
  || isInternalProcessing || isLocationsCountriesLoading;

  const memoizedGroupItems = useMemo(() => geoDataGroupItems?.data || [], [geoDataGroupItems]);
  const memoizedDefaultItems = useMemo(() => geographicDefaultItems, [geographicDefaultItems]);

  const onSortChange = (id: string) => {
    setOrderBy((prev) => {
      const hasId = prev.includes(id);
      const hasNegativeId = prev.includes(`-${id}`);
      if (hasNegativeId) {
        return prev.filter((item) => item !== `-${id}`);
      }
      if (hasId) {
        return prev.map((item) => (item === id ? `-${id}` : item));
      }
      return [id];
    });
    setOffset(0);
  };

  return (
    <MaterialityConfigGeographicView
      items={memoizedGroupItems}
      defaultItems={memoizedDefaultItems}
      isEmptyList={valueToggle ? memoizedGroupItems.length === 0 : geographicDefaultItems.length === 0}
      loading={loading}
      isLoadingMore={isLoadingMore}
      onOpenModal={onOpenModal}
      goBack={goBack}
      saveCompetitor={saveCompetitor}
      openModal={openModal}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      valueToggle={valueToggle}
      onChangeToggle={onChangeToggle}
      onDeleteItem={onDeleteItem}
      isDeleteModalOpen={isDeleteModalOpen}
      onCancel={onCancel}
      onDeleteConfirm={onDeleteConfirm}
      onUpdate={onUpdate}
      recordId={recordId}
      vcmId={Number(vcmId)}
      setOffset={setOffset}
      setOpenModal={setOpenModal}
      observeStatus={observeStatus}
      dateUpdated={stateAIData?.data?.lastSearch}
      onSortChange={onSortChange}
      orderBy={orderBy}
      locationsCountries={locationsCountries?.data || []}
      refreshAffectedToken={refreshAffectedToken}
      isViewOnly={isViewOnly}
      isAffectedDataOutdated={isAffectedDataOutdated}
      isLastSearchOutdated={isLastSearchOutdated}
      isRefreshProcessing={isRefreshProcessing}
      disableNext={disableNext}
      disableSave={disableSave}
    />
  );
}

export default MaterialityConfigGeographic;
